import { DatapointStatus, type PerformanceDatapointType } from "owa-analytics-types";
import { getMailboxKeyForMailboxInfo, type MailboxInfo } from "owa-client-types";

export interface SearchOfflineFallbackResult {
    status: DatapointStatus;
    resultCount?: number;
    errorReason?: string;
}

interface SearchOfflineFallbackContinuation {
    mailboxInfo: MailboxInfo;
    complete?: (result: SearchOfflineFallbackResult) => void;
}

const pendingFallbacks = new WeakMap<PerformanceDatapointType, SearchOfflineFallbackContinuation>();

export function waitForSearchOfflineFallback(datapoint: PerformanceDatapointType, mailboxInfo: MailboxInfo): Promise<SearchOfflineFallbackResult> {
    return new Promise(resolve => {
        pendingFallbacks.set(datapoint, { mailboxInfo, complete: resolve });
    });
}

export function getSearchOfflineFallbackMailboxInfo(datapoint: PerformanceDatapointType | undefined): MailboxInfo | undefined {
    return datapoint ? pendingFallbacks.get(datapoint)?.mailboxInfo : undefined;
}

export function abandonSearchOfflineFallbackIfMailboxChanged(datapoint: PerformanceDatapointType | undefined, currentMailboxInfo: MailboxInfo): boolean {
    const mailboxInfo = getSearchOfflineFallbackMailboxInfo(datapoint);
    if (!mailboxInfo ||
        getMailboxKeyForMailboxInfo(mailboxInfo) === getMailboxKeyForMailboxInfo(currentMailboxInfo)) {
        return false;
    }
    
    datapoint?.end(undefined, DatapointStatus.UserCancelled);
    completeSearchOfflineFallback(datapoint, { status: DatapointStatus.UserCancelled });
    return true;
}

export function completeSearchOfflineFallback(datapoint: PerformanceDatapointType | undefined, result: SearchOfflineFallbackResult): void {
    if (datapoint) {
        const pending = pendingFallbacks.get(datapoint);
        const complete = pending?.complete;
        // Late-loading handlers still need the originating mailbox after completion.
        if (pending) {
            pending.complete = undefined;
        }
        complete?.(result);
    }
}
